import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Check, 
  Zap, 
  Crown,
  ArrowRight,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Pricing = () => {
  const { t, isRTL } = useLanguage();
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/subscriptions/plans`);
        setPlans(response.data.plans);
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      }
    };
    fetchPlans();
  }, []);

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/subscriptions/subscribe`,
        {
          subscription_type: planId,
          payment_method: 'stripe'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Abonnement activé (mode test)');
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const featuresMap = {
    basic_booking: 'Réservation de base',
    email_reminders: 'Rappels par email',
    priority_waitlist: 'Liste d\'attente prioritaire',
    sms_notifications: 'Notifications SMS',
    appointment_history: 'Historique des RDV',
    multi_professional: 'Multi-praticiens',
    unlimited_appointments: 'RDV illimités',
    analytics_dashboard: 'Tableau de bord analytics',
    patient_management: 'Gestion des patients',
    custom_notifications: 'Notifications personnalisées',
    api_access: 'Accès API',
    teleconsultation: 'Téléconsultation vidéo',
    calendar_sync: 'Synchronisation calendrier',
    basic_analytics: 'Analytics basiques',
    waitlist_smart: 'Waitlist intelligente',
    ai_voice_assistant: 'Assistant vocal IA',
    voice_cloning: 'Clonage de voix',
    whatsapp_booking: 'Réservation WhatsApp',
    multi_cabinet: 'Multi-cabinet',
    migration_sync: 'Migration Doctolib/Doctena',
    contract_signing: 'Signature de contrat',
    priority_support: 'Support prioritaire',
    no_ads: 'Sans publicité',
    priority_alerts: 'Alertes prioritaires',
    chat_support: 'Support par chat',
    document_storage: 'Stockage documents'
  };

  const getPlanDescription = (planId) => {
    const descriptions = {
      'free': 'Accès basique pour découvrir la plateforme',
      'premium_patient': 'Pour les patients qui veulent un accès prioritaire et des fonctionnalités avancées',
      'pro_basic': 'Pour les professionnels qui démarrent avec la gestion de cabinet en ligne',
      'pro_smart': 'Gestion complète avec téléconsultation et SMS pour les professionnels actifs',
      'pro_premium': 'Toutes les fonctionnalités incluant IA, WhatsApp et multi-cabinet'
    };
    return descriptions[planId] || '';
  };

  const getPlanIcon = (planId) => {
    if (planId === 'free') return null;
    if (planId === 'premium_patient') return <Zap className="w-5 h-5" />;
    if (planId === 'pro_basic') return <Zap className="w-5 h-5" />;
    if (planId === 'pro_smart') return <Crown className="w-5 h-5" />;
    return <Crown className="w-5 h-5 text-amber-500" />;
  };

  const getPlanColor = (planId) => {
    if (planId === 'free') return 'bg-slate-100';
    if (planId === 'premium_patient') return 'bg-blue-50';
    if (planId === 'pro_basic') return 'bg-primary/10';
    if (planId === 'pro_smart') return 'bg-purple-50';
    return 'bg-gradient-to-br from-amber-50 to-orange-50';
  };

  return (
    <div className="min-h-screen bg-white py-12 md:py-20" data-testid="pricing-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            {t('pricing_title')}
          </h1>
          <p className="text-slate-600 max-w-xl mx-auto">
            Choisissez le plan qui correspond à vos besoins. Tous les plans premium incluent 14 jours d'essai gratuit.
          </p>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative overflow-hidden ${
                plan.id === 'premium_professional' || plan.id === 'pro_smart' ? 'border-2 border-primary shadow-lg' : ''
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {(plan.id === 'premium_professional' || plan.id === 'pro_smart') && (
                <div className="absolute top-4 right-4">
                  <Badge className="bg-primary text-white">Populaire</Badge>
                </div>
              )}
              
              <CardHeader className={`${getPlanColor(plan.id)} pb-8`}>
                <div className="flex items-center gap-2 mb-2">
                  {getPlanIcon(plan.id)}
                  <CardTitle className="text-xl text-slate-900">
                    {plan.name?.fr || plan.id}
                  </CardTitle>
                </div>
                <CardDescription className="text-slate-600">
                  {getPlanDescription(plan.id)}
                </CardDescription>
                {plan.trial_days && (
                  <p className="text-primary font-medium text-sm mt-1">
                    {plan.trial_days} jours d'essai gratuit
                  </p>
                )}
                <div className="mt-4">
                  <span className="text-4xl font-bold text-slate-900">
                    {plan.price}€
                  </span>
                  {plan.price > 0 && (
                    <span className="text-slate-500">{t('pricing_per_month')}</span>
                  )}
                </div>
              </CardHeader>

              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {plan.features?.map((feature, idx) => (
                    <li key={idx} className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600">
                        {featuresMap[feature] || feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter className="pt-4">
                {plan.id === 'free' ? (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => navigate('/register')}
                    data-testid="free-plan-btn"
                  >
                    Commencer gratuitement
                  </Button>
                ) : (
                  <Button 
                    className={`w-full ${plan.id === 'premium_professional' ? 'btn-shine' : ''}`}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loading}
                    data-testid={`subscribe-${plan.id}`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 me-2 animate-spin" />
                        {t('loading')}
                      </>
                    ) : (
                      <>
                        {t('pricing_subscribe')}
                        <ArrowRight className={`w-4 h-4 ${isRTL ? 'me-2 rotate-180' : 'ms-2'}`} />
                      </>
                    )}
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* FAQ or Additional Info */}
        <div className="mt-16 text-center">
          <p className="text-slate-500 text-sm">
            * Paiements sécurisés par Stripe. PayPal sera disponible prochainement.
          </p>
          <p className="text-slate-500 text-sm mt-2">
            Questions ? Contactez-nous à contact@drbooking.pro
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
